import React from 'react'
import TextField from '@material-ui/core/TextField'
import {makeStyles} from '@material-ui/core/styles'
import type {FlexProps} from '@chakra-ui/react'

import {Flex, Text} from '../../utils/chakraMonomorphic'
import {browserSupportsInputType} from '../../utils'
import {TimeInputFallback} from '../TimeInputFallback/TimeInputFallback'

interface Props extends Omit<FlexProps, 'onChange'> {
  value: string
  onChange(militaryTime: string): void
}

// NOTE: `makeStyles` must be called once at module scope, not inside the
// component body. Calling it on every render creates a new JSS stylesheet
// manager each time; under React 18's unmount timing that manager is torn
// down with a stale/undefined ref, throwing
// `Cannot read properties of undefined (reading 'refs')` from MUI v4's JSS
// `detach`, which unmounts the whole modal tree (blank page).
const useStyles = makeStyles(() => ({
  textField: {
    border: '1px solid #ced2d5',
    borderRadius: '3px',
    width: '100%',
  },
  root: {
    height: '38px',
    justifyContent: 'flex-end',
    paddingLeft: '1rem',

    '& input::-webkit-clear-button': {
      '-webkitAppearance': 'none',
    },
  },

  underline: {
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
}))

export const AlbumTimeControl: React.FC<React.PropsWithChildren<Props>> = ({
  value,
  onChange,
  ...rest
}) => {
  const classes = useStyles()

  const InputProps = {
    classes: {
      root: classes.root,
      underline: classes.underline,
    },
    required: true,
    steps: 900,
  }

  return (
    <Flex direction='column' {...rest}>
      <Text fontWeight='bold' pt={4} fontSize='16px'>
        Time
      </Text>

      <Flex width={['100%']} mt={[1, 0]} pt={1}>
        {browserSupportsInputType('time') ? (
          <TextField
            id='time'
            placeholder='enter time'
            type='time'
            data-testid='time input'
            variant='standard'
            value={value}
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={InputProps}
            onChange={(e): void => onChange(e.target.value)}
          />
        ) : (
          <TimeInputFallback value={value} onChange={onChange} />
        )}
      </Flex>
    </Flex>
  )
}
